import React, { useEffect, useState } from 'react';
import { useParams, useOutletContext, Link } from 'react-router-dom';

import { db } from './firebase'

import { collection, addDoc, updateDoc, deleteDoc, doc, query, orderBy } from "firebase/firestore";
import { useCollection, useDocument } from 'react-firebase-hooks/firestore';

import { useHuntPlayers, nameOf, MAX_MEMBERS } from './members';

export default function Manage() {
    const { uid } = useOutletContext();
    const { huntId } = useParams();

    const huntRef = doc(db, "hunts", huntId);
    const [hunt, huntLoading, huntError] = useDocument(huntRef);

    const memberIds = hunt ? (hunt.data()?.memberIds ?? null) : undefined;
    const [players] = useHuntPlayers(memberIds);

    const itemsRef = collection(db, "hunts", huntId, "items");
    const [items] = useCollection(query(itemsRef, orderBy("author")));

    // Form state
    const [author, setAuthor] = useState("");
    const [newItems, setNewItems] = useState("");
    const [startInput, setStartInput] = useState("");
    const [endInput, setEndInput] = useState("");
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        const data = hunt?.data();
        if (data?.startDate && data?.endDate) {
            setStartInput(new Date(data.startDate.seconds * 1000).toISOString().split("T")[0]);
            setEndInput(new Date(data.endDate.seconds * 1000).toISOString().split("T")[0]);
        }
    }, [hunt]);

    // Default the item author to the owner (you).
    useEffect(() => {
        if (uid) setAuthor((a) => a || uid);
    }, [uid]);

    if (huntLoading) {
        return <p>Loading…</p>;
    }
    if (huntError) {
        return <p>Error: {huntError.message}</p>;
    }
    if (!hunt.exists()) {
        return <p>Hunt not found.</p>;
    }

    const data = hunt.data();

    if (data.ownerId !== uid) {
        return <p>Only the hunt owner can manage it. <Link to={"/play/" + huntId}>Go play</Link></p>;
    }

    const nameMap = new Map((players?.docs ?? []).map((p) => [p.id, nameOf(p)]));
    const inviteUrl = `${window.location.origin}/join/${huntId}`;

    async function handleAddItems(e) {
        e.preventDefault();
        const lines = newItems.trim().split("\n").map((s) => s.trim()).filter(Boolean);
        if (lines.length === 0) return;
        await Promise.all(lines.map((desc) => addDoc(itemsRef, { desc, author })));
        setNewItems("");
    }

    async function handleSaveDates(e) {
        e.preventDefault();
        // + T00:00 forces JS to interpret the date as local midnight.
        await updateDoc(huntRef, {
            startDate: new Date(startInput + "T00:00"),
            endDate: new Date(endInput + "T00:00"),
        });
    }

    async function copyInvite() {
        try {
            await navigator.clipboard.writeText(inviteUrl);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        } catch {
            /* clipboard may be unavailable; the field is selectable as a fallback */
        }
    }

    return (
        <div>
            <p><Link to="/">← Back</Link></p>
            <h1>Manage: {data.displayName}</h1>
            <p>
                <Link to={"/play/" + huntId}>Play view</Link>
                {" · "}
                <Link to={"/gallery/" + huntId}>Gallery</Link>
            </p>

            <h3>Invite players</h3>
            <p>Share this link — anyone who opens it and signs in joins the hunt:</p>
            <input
                readOnly
                value={inviteUrl}
                style={{ width: "100%", maxWidth: 480 }}
                onFocus={(e) => e.target.select()}
            />
            <button onClick={copyInvite} style={{ marginLeft: 6 }}>
                {copied ? "Copied!" : "Copy"}
            </button>

            <h3>Members ({(data.memberIds ?? []).length} / {MAX_MEMBERS})</h3>
            <ul>
                {(players?.docs ?? []).map((p) => (
                    <li key={p.id}>{nameOf(p)}{p.id === data.ownerId ? " (owner)" : ""}</li>
                ))}
            </ul>

            <h3>Items</h3>
            <ul>
                {items && items.docs.map((it) => (
                    <li key={it.id}>
                        {it.data().desc} <i>({nameMap.get(it.data().author) ?? it.data().author})</i>
                        <button onClick={() => deleteDoc(it.ref)} style={{ marginLeft: 6 }}>✕</button>
                    </li>
                ))}
            </ul>

            <form onSubmit={handleAddItems}>
                Author:{" "}
                <select value={author} onChange={(e) => setAuthor(e.target.value)}>
                    {(players?.docs ?? []).map((p) => (
                        <option key={p.id} value={p.id}>{nameOf(p)}</option>
                    ))}
                </select>
                <p>One item per line:</p>
                <textarea value={newItems} onChange={(e) => setNewItems(e.target.value)} />
                <br />
                <button type="submit">Add items</button>
            </form>

            <h3>Dates</h3>
            <form onSubmit={handleSaveDates}>
                Start <input type="date" value={startInput} onChange={(e) => setStartInput(e.target.value)} />
                {" "}
                End <input type="date" value={endInput} onChange={(e) => setEndInput(e.target.value)} />
                <br />
                <button type="submit">Save dates</button>
            </form>
        </div>
    );
}
